import React, { useEffect, useState } from "react";
import Breadcrumb from "../../components/common/BreadCrumb";
import ReactSelect from "react-select";
import ReactDatePicker from "react-datepicker";
import DebounceSearch from "../../components/common/DebounceSearch";
import Action from "../../components/common/Action";
import ConfirmationModel from "../../components/common/ConfirmationModel";
import {
  NotificationDelete,
  getNotification,
  updateNotification,
} from "../../redux/notification/slice";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import RippleLoader from "../../components/loader/RippleLoader";
import TableShowLimit from "../../components/common/TableShowLimit";
import Pagination from "../../components/common/Pagination";

const Notification = () => {
  const dispatch = useDispatch();
  const [selectedBulk, setSelectedBulk] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const [searchValue, setSearchValue] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pageLimit, setPageLimit] = useState(10);
  const [pageCount, setPageCount] = useState(1);
  const [sortBy, setSortBy] = useState("");
  const [selectedSort, setSelectedSort] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const DateRange =
    dateRange[0] && dateRange[1]
      ? `${moment(dateRange[0]).format("DD/MM/YYYY")} - ${moment(
          dateRange[1]
        ).format("DD/MM/YYYY")}`
      : "";

  // Selector
  const { getNotificationData, loading } = useSelector((store) => ({
    getNotificationData:
      store?.notificationData?.getNotificationStatus?.data?.data,
    loading: store?.notificationData?.loading,
  }));

  useEffect(() => {
    setSelectedIds([]);
    setSelectedRows([]);
    setSelectedBulk("");
    dispatch(
      getNotification({
        search: searchValue,
        pageCount: pageCount,
        pageLimit: pageLimit,
        sort_by: sortBy,
        date_range: DateRange || "",
      })
    );
  }, [searchValue, pageCount, pageLimit, sortBy, DateRange]);

  const handleCheckboxChange = (event, id) => {
    const checked = event.target.checked;

    if (checked) {
      const selectedItem = getNotificationData?.data?.find(
        (item) => item.id === id
      );
      if (selectedItem) {
        setSelectedRows((prev) => [...prev, selectedItem]);
      }
    } else {
      setSelectedRows((prev) => prev.filter((item) => item.id !== id));
    }

    setSelectedIds((prev) =>
      checked ? [...prev, id] : prev.filter((i) => i !== id)
    );
  };

  const handleDelete = () => {
    setIsLoading(true);

    dispatch(NotificationDelete({ ids: selectedIds })).then((res) => {
      if (res.type === "NotificationDelete/fulfilled") {
        dispatch(
          getNotification({
            search: searchValue,
            pageCount: pageCount,
            pageLimit: pageLimit,
            sort_by: sortBy,
            date_range: DateRange || "",
          })
        );
        setIsLoading(false);
        setConfirmDelete(false);
        setSelectedIds([]);
        setSelectedRows([]);
        setSelectedBulk("");
      } else {
        setIsLoading(false);
      }
    });
  };

  const handleMarkStatus = async (ids, is_read) => {
    await dispatch(updateNotification({ data: { ids, is_read } }));
    dispatch(
      getNotification({
        search: searchValue,
        pageCount: pageCount,
        pageLimit: pageLimit,
        sort_by: sortBy,
        date_range: DateRange || "",
      })
    );
    setSelectedRows([]);
    setSelectedIds([]);
    setSelectedBulk("");
  };

  const SortOption = [
    { value: "newest_first", label: "Newest First" },
    { value: "oldest_first", label: "Oldest First" },
  ];

  const bulkOptions = () => {
    const options = [];
    const addedActions = new Set();

    const allAreUnread = selectedRows.every((item) => item.is_read === 0);
    const allAreRead = selectedRows.every((item) => item.is_read === 1);

    if (selectedRows.length > 0) {
      const deleteAction = {
        value: "delete",
        label: "Delete",
        action: () => {
          const selectedIds = selectedRows?.map((item) => item.id);
          setSelectedIds(selectedIds);
          setConfirmDelete(true);
        },
      };
      if (!addedActions.has(deleteAction.value)) {
        options.push(deleteAction);
        addedActions.add(deleteAction.value);
      }

      if (allAreUnread) {
        const markAsViewed = {
          value: "Mark As Viewed",
          label: "Mark As Viewed",
          action: () => {
            const selectedIds = selectedRows?.map((item) => item.id);
            handleMarkStatus(selectedIds, "1");
          },
        };
        options.push(markAsViewed);
        addedActions.add("Mark As Viewed");
      }

      if (allAreRead) {
        const markAsUnread = {
          value: "Mark As Unread",
          label: "Mark As Unread",
          action: () => {
            const selectedIds = selectedRows?.map((item) => item.id);
            handleMarkStatus(selectedIds, "0");
          },
        };
        options.push(markAsUnread);
        addedActions.add("Mark As Unread");
      }
    }
    return options;
  };

  return (
    <>
      <div>
        <div className="flex justify-between items-start">
          <div>
            <h6 className="font-Montserrat font-medium text-xl mb-3">
              Notifications
            </h6>
            <Breadcrumb
              breadCrumbParent={"Home"}
              breadCrumbActive={"Notifications"}
              breadCrumbTitleKey={"/"}
            />
          </div>
        </div>
        <div>
          <p className="text-primaryBlack text-3xl font-Montserrat my-6">
            All Notification ({getNotificationData?.data?.length})
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full">
          {/* Bulk Action */}
          <div className="w-full sm:w-[200px] md:w-[220px] min-w-[160px]">
            <ReactSelect
              className="w-full text-black p-0 font-Proxima"
              id="option"
              placeholder="Bulk Action"
              options={bulkOptions(selectedRows)}
              isClearable
              value={selectedBulk}
              isDisabled={!selectedRows.length}
              onChange={(selected) => {
                setSelectedBulk(selected);
                if (selected?.action) {
                  selected.action();
                }
              }}
              styles={{
                control: (base) => ({
                  ...base,
                  border: `1px solid #0000003B`,
                  borderColor: selectedBulk ? "#F15B29" : "#0000003B",
                  borderRadius: "0.3rem",
                  boxShadow: "none",
                  "&:hover": { border: "1px solid #0000003B" },
                }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isSelected ? "#F15B29" : "white",
                  "&:active": {
                    backgroundColor: state.isSelected
                      ? "#F15B29"
                      : "rgba(0, 0, 0, 0.04)",
                  },
                }),
              }}
            />
          </div>

          {/* Date Range */}
          <div className="w-full sm:w-[200px] md:w-[220px] min-w-[160px]">
            <div className="text-black border border-borderTheme/80 flex justify-between relative rounded w-full px-2 py-[6px] items-center transition duration-300 ease-in-out">
              <ReactDatePicker
                selected={dateRange[0]}
                onChange={(dates) => setDateRange(dates)}
                selectsRange
                isClearable
                startDate={dateRange[0]}
                endDate={dateRange[1]}
                maxDate={new Date()}
                dateFormat="dd/MM/yyyy"
                placeholderText="Date Range"
                className="w-full"
              />
            </div>
          </div>

          {/* Search */}
          <div className="w-full sm:flex-1 min-w-[180px]">
            <DebounceSearch
              className="w-full"
              search={searchValue}
              handleChange={setSearchValue}
              placeholder="Search..."
            />
          </div>

          {/* Sort By */}
          <div className="w-full sm:w-[200px] md:w-[220px] min-w-[160px]">
            <ReactSelect
              className="w-full text-black p-0 font-Proxima"
              id="sort"
              placeholder="Sort By"
              options={SortOption}
              isClearable
              value={selectedSort}
              onChange={(e) => {
                setSelectedSort(e);
                setSortBy(e ? e.value : "");
              }}
              styles={{
                control: (base) => ({
                  ...base,
                  border: `1px solid #0000003B`,
                  borderColor: selectedSort ? "#F15B29" : "#0000003B",
                  borderRadius: "0.3rem",
                  boxShadow: "none",
                  "&:hover": { border: "1px solid #0000003B" },
                }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isSelected ? "#F15B29" : "white",
                  "&:active": {
                    backgroundColor: state.isSelected
                      ? "#F15B29"
                      : "rgba(0, 0, 0, 0.04)",
                  },
                }),
              }}
            />
          </div>
        </div>

        {loading ? (
          <RippleLoader />
        ) : (
          <div>
            <div className="mt-6 space-y-4 bg-white rounded-lg">
              {getNotificationData?.data.length > 0 ? (
                getNotificationData?.data.map((item, index) => (
                  <>
                    <div className="flex items-center gap-3">
                      {/* Index */}
                      <div className="text-base font-medium w-5 text-right font-Montserrat">
                        {index + 1}.
                      </div>
                      <div
                        key={item.id}
                        className={`flex items-start gap-4 p-4 rounded-lg w-[100%] hover:bg-bgLightTheme ${
                          selectedIds.includes(item.id)
                            ? "bg-primaryOrange"
                            : "bg-light_background_default"
                        }`}
                      >
                        {/* Checkbox */}
                        <div className="pt-1.5">
                          <input
                            type="checkbox"
                            className="w-5 h-5 mr-2 accent-themeBtn"
                            onChange={(e) => handleCheckboxChange(e, item.id)}
                            checked={selectedIds.includes(item.id)}
                          />
                        </div>

                        {/* Notification content */}
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-base  text-brown">
                              {item.title}
                            </p>
                            {/* badge */}
                            {item?.is_read === 0 ? (
                              <span className="w-2 h-2 rounded-[50%] bg-bgGreen mb-3"></span>
                            ) : (
                              ""
                            )}
                          </div>
                          <p className="text-sm text-primaryBlack mt-1 line-clamp-2">
                            {item.description}
                          </p>
                          <p className="text-xs text-primaryBlack mt-2">
                            {moment(item.created_at).format(
                              "DD/MM/YYYY | hh:mm A"
                            )}
                          </p>
                        </div>
                        <Action
                          optionList={
                            <>
                              {item?.is_read === 1 ? (
                                <li
                                  className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                                  onClick={() =>
                                    handleMarkStatus([item.id], "0")
                                  }
                                >
                                  Mark As Unread
                                </li>
                              ) : (
                                <li
                                  className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                                  onClick={() =>
                                    handleMarkStatus([item.id], "1")
                                  }
                                >
                                  Mark As Viewed
                                </li>
                              )}

                              <li
                                className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                                onClick={() => {
                                  setSelectedIds([item.id]);
                                  setConfirmDelete(true);
                                }}
                              >
                                Delete
                              </li>
                            </>
                          }
                        />
                      </div>
                    </div>
                  </>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center h-full ">
                  <p className="text-2xl  text-primaryBlack">
                    “No New Notification”
                  </p>
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-end my-2 ">
              <TableShowLimit
                setPaginationCurrentLimit={setPageLimit}
                paginationCurrentLimit={pageLimit}
              />
              {getNotificationData?.data &&
                getNotificationData?.data.length > 0 && (
                  <p className="text-xs font-Proxima pl-2">
                    {(getNotificationData?.current_page - 1) *
                      getNotificationData?.per_page +
                      1}{" "}
                    -{" "}
                    {(getNotificationData?.current_page - 1) *
                      getNotificationData?.per_page +
                      getNotificationData?.data?.length}{" "}
                    of {getNotificationData?.total}
                  </p>
                )}

              <div className="px-4 py-2 rounded-lg ">
                <Pagination
                  current={pageCount}
                  total={getNotificationData?.total}
                  pagination={(crPage) => {
                    setPageCount(crPage);
                  }}
                  lastPage={Math.ceil(getNotificationData?.total / pageLimit)}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      {confirmDelete && (
        <ConfirmationModel
          confirmText={"Delete"}
          onClose={() => {
            setConfirmDelete(false);
            setSelectedIds([]);
          }}
          onConfirm={handleDelete}
        />
      )}
    </>
  );
};

export default Notification;
